// Pure helpers of Administration → Moteurs → « Connexion des comptes » (no DOM; contract in
// docs/wip/SPEC-CONNEXIONS.md): normalisation of GET /api/engines/accounts and of login jobs, the
// display of each account (state, badge, buttons), the renewal warning, the checks that mirror the
// server (Claude code, Gemini and Kie.ai keys, login page address) and the manual Terminal procedures: the
// hosted instance's (docs/DEPLOY-RAILWAY.md §7) and, when the office does not log the accounts in
// itself, the Mac mini's (docs/DEPLOY-MACMINI.md §4). Tested in test/ui-admin-accounts.test.js.
import { t } from '../../i18n/fr.js';
import { isSafeUrlAttr } from '../../dom.js';
import './strings.js';

/** Accounts that are connected by a login window (Gemini and Kie.ai take a key). */
export const LOGIN_ENGINES = ['claude', 'codex'];
/** Job states, in the order the server walks them (docs/wip/SPEC-CONNEXIONS.md, objet `job`). */
export const JOB_STATES = ['starting', 'waiting_code', 'checking', 'waiting_user', 'done', 'error', 'expired', 'cancelled'];
/** States of a job that is still running (the login window polls while it is in one of them). */
export const JOB_ACTIVE_STATES = ['starting', 'waiting_code', 'checking', 'waiting_user'];
/** Active states in which closing the window cancels the job (not `checking`: the CLI is finishing). */
export const JOB_CANCELLABLE_STATES = ['starting', 'waiting_code', 'waiting_user'];
/** Same limit as the server for the pasted Claude code. */
export const CODE_MAX = 2000;
/** Same rule as the server for a Gemini API key. */
// Classic keys start with AIza; newer ones look like « AQ.Ab8… » (dots allowed).
export const GEMINI_KEY_RE = /^[A-Za-z0-9._-]{20,200}$/;
/** Where a free Gemini key is created. */
export const GEMINI_KEY_URL = 'https://aistudio.google.com/app/apikey';
/** Same rule as the server for a Kie.ai API key. */
export const KIE_KEY_RE = /^[A-Za-z0-9._-]{16,200}$/;
/** Where a Kie.ai key is created. */
export const KIE_KEY_URL = 'https://kie.ai/api-key';
/** Accounts that take an API key (a form on their card). */
export const KEY_ENGINES = ['gemini', 'kie'];
/** Below this many days before the Claude refresh token expires, the card asks to reconnect. */
export const RENEW_WARN_DAYS = 7;
/** Lifetime of the one-time code of `codex login --device-auth` (the job itself lasts 16 min). */
export const CODEX_CODE_MS = 15 * 60_000;
/** Login pages the server accepts per engine (the client checks them again before linking). */
export const LOGIN_URL_PREFIXES = Object.freeze({
  claude: ['https://claude.com/', 'https://claude.ai/', 'https://platform.claude.com/'],
  codex: ['https://auth.openai.com/'],
});

const DAY_MS = 86_400_000;
const CLAUDE_STATES = ['account', 'token', 'none', 'unknown', 'error'];
const CODEX_STATES = ['chatgpt', 'apikey', 'none', 'unknown', 'error'];
const KEY_STATES = ['env', 'stored', 'none', 'unknown'];
const KEY_ENV_DEFAULTS = { gemini: 'GEMINI_API_KEY', kie: 'KIE_API_KEY' };
// eslint-disable-next-line no-control-regex
const CONTROL_RE = /[\u0000-\u001f\u007f-\u009f\u2028\u2029]/;
// eslint-disable-next-line no-control-regex
const CONTROL_RE_G = /[\u0000-\u001f\u007f-\u009f\u2028\u2029]+/g;
/** Subscription names as shown after the e-mail ("Compte Claude connecté · … · Max"). */
const PLANS = { max: 'Max', pro: 'Pro', team: 'Team', enterprise: 'Enterprise', free: 'Gratuit', plus: 'Plus', business: 'Business' };

function str(v) {
  return typeof v === 'string' ? v : v == null ? '' : String(v);
}

/** A short single line of server text (no control characters, capped). */
function line(v, max = 300) {
  const s = typeof v === 'string' ? v.replace(CONTROL_RE_G, ' ').replace(/\s+/g, ' ').trim() : '';
  return s.length > max ? `${s.slice(0, max - 1).trimEnd()}…` : s;
}

/** Epoch milliseconds from a number (seconds accepted) or a date string; null otherwise. */
function epochMs(v) {
  if (typeof v === 'number' && Number.isFinite(v) && v > 0) return v < 1e12 ? v * 1000 : v;
  if (typeof v === 'string' && v.trim()) {
    const n = Date.parse(v);
    return Number.isFinite(n) ? n : null;
  }
  return null;
}

function pick(value, allowed, fallback) {
  return allowed.includes(value) ? value : fallback;
}

// ---------------------------------------------------------------------------------------------
// Jobs

/**
 * Normalises a login job (`{id, engine, state, url, userCode, startedAt, expiresAt, finishedAt,
 * message, account}`). An unknown state reads as `error`, so a window never polls forever.
 * @param {unknown} raw
 * @param {string} [engine] the engine asked for (used when the job does not say)
 * @returns {object|null} null when `raw` is not an object
 */
export function normalizeJob(raw, engine = '') {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null;
  const account = raw.account && typeof raw.account === 'object'
    ? { email: line(raw.account.email, 200), subscription: line(raw.account.subscription, 40) }
    : null;
  return {
    id: line(raw.id, 100),
    engine: LOGIN_ENGINES.includes(raw.engine) ? raw.engine : (LOGIN_ENGINES.includes(engine) ? engine : ''),
    state: pick(raw.state, JOB_STATES, 'error'),
    url: typeof raw.url === 'string' ? raw.url : '',
    userCode: line(raw.userCode, 40),
    startedAt: epochMs(raw.startedAt),
    expiresAt: epochMs(raw.expiresAt),
    finishedAt: epochMs(raw.finishedAt),
    message: line(raw.message, 500),
    account: account && (account.email || account.subscription) ? account : null,
  };
}

/**
 * The job carried by a login response (`{job}`, `{job: null}` or a bare job).
 * @returns {object|null}
 */
export function unwrapJob(res, engine = '') {
  if (!res || typeof res !== 'object') return null;
  if (Object.prototype.hasOwnProperty.call(res, 'job')) return normalizeJob(res.job, engine);
  return res.state ? normalizeJob(res, engine) : null;
}

/** A job built by the page itself ("starting" before the server answered, or a local failure). */
export function localJob(engine, state, message = '') {
  return normalizeJob({ engine, state, message }, engine);
}

/** True while the job runs (the window polls). */
export function isJobActive(job) {
  return !!job && JOB_ACTIVE_STATES.includes(job.state);
}

/** True when closing the window should cancel the job (DELETE /api/engines/login/:engine). */
export function isJobCancellable(job) {
  return !!job && JOB_CANCELLABLE_STATES.includes(job.state);
}

/**
 * What the status line of a login window says about a job: the server's message when it has
 * one, else a default per state.
 * @returns {{text: string, tone: 'info'|'error'|'success'|'warn'}}
 */
export function jobMessage(job) {
  if (!job) return { text: '', tone: 'info' };
  const engine = job.engine === 'codex' ? 'codex' : 'claude';
  const failed = ['error', 'expired', 'cancelled'].includes(job.state);
  // A message on a finished login is a caveat (e.g. the CLAUDE_CODE_OAUTH_TOKEN variable): it
  // already says the account is connected, and replaces the success line.
  if (job.state === 'done') {
    return job.message ? { text: job.message, tone: 'warn' } : { text: t(`adm.acc.job.done.${engine}`), tone: 'success' };
  }
  const tone = failed || (job.state === 'waiting_code' && job.message) ? 'error' : 'info';
  if (job.message) return { text: job.message, tone };
  switch (job.state) {
    case 'starting': return { text: t('adm.acc.job.starting'), tone };
    case 'waiting_user': return { text: t('adm.acc.job.waitingUser'), tone };
    case 'checking': return { text: t(`adm.acc.job.checking.${engine}`), tone };
    case 'error': return { text: t('adm.acc.job.error'), tone };
    case 'expired': return { text: t('adm.acc.job.expired'), tone };
    case 'cancelled': return { text: t('adm.acc.job.cancelled'), tone };
    default: return { text: '', tone };
  }
}

/**
 * The login page address of a job when it is safe to link: https, accepted by dom.js, and on one
 * of the pages the server accepts for this engine. null otherwise.
 * @param {string} engine
 * @param {unknown} url
 */
export function safeLoginUrl(engine, url) {
  const s = typeof url === 'string' ? url.trim() : '';
  if (!s || s.length > 4096 || /[\s\u0000-\u001f\u007f]/.test(s)) return null;
  if (!/^https:\/\//i.test(s) || !isSafeUrlAttr('href', s)) return null;
  const prefixes = Object.prototype.hasOwnProperty.call(LOGIN_URL_PREFIXES, engine) ? LOGIN_URL_PREFIXES[engine] : [];
  return prefixes.some((p) => s.startsWith(p)) ? s : null;
}

/**
 * When the one-time code of a Codex job stops working: CODEX_CODE_MS after the start, and never
 * after the job's own expiry. null when the job does not say.
 * @param {{startedAt?: number|null, expiresAt?: number|null}|null} job
 * @returns {number|null} epoch ms (server clock)
 */
export function codexCodeExpiry(job) {
  const ends = [job?.expiresAt, job?.startedAt ? job.startedAt + CODEX_CODE_MS : null].filter((v) => Number.isFinite(v) && v > 0);
  return ends.length ? Math.min(...ends) : null;
}

/** "m:ss" for a countdown (never negative). */
export function formatCountdown(ms) {
  const total = Math.max(0, Math.ceil((Number(ms) || 0) / 1000));
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
}

// ---------------------------------------------------------------------------------------------
// Accounts

/**
 * Normalises GET /api/engines/accounts (and the answers of logout / gemini-key, which return the
 * same shape). Missing or unknown values fall back on "unknown" states.
 * @param {unknown} res
 * @returns {{loginsEnabled: boolean, mock: boolean, claude: object, codex: object, gemini: object,
 *   jobs: {claude: object|null, codex: object|null}, checkedAt: number|null}}
 */
export function normalizeAccounts(res) {
  const r = res && typeof res === 'object' && !Array.isArray(res) ? res : {};
  const c = r.claude && typeof r.claude === 'object' ? r.claude : {};
  const x = r.codex && typeof r.codex === 'object' ? r.codex : {};
  const jobs = r.jobs && typeof r.jobs === 'object' ? r.jobs : {};
  return {
    loginsEnabled: r.loginsEnabled === true,
    mock: r.mock === true,
    claude: {
      state: pick(c.state, CLAUDE_STATES, 'unknown'),
      email: line(c.email, 200),
      organization: line(c.organization, 200),
      subscription: line(c.subscription, 40),
      refreshExpiresAt: epochMs(c.refreshExpiresAt),
      tokenVarSet: c.tokenVarSet === true,
      detail: line(c.detail, 500),
    },
    codex: {
      state: pick(x.state, CODEX_STATES, 'unknown'),
      email: line(x.email, 200),
      subscription: line(x.subscription, 40),
      keyVarSet: x.keyVarSet === true,
      detail: line(x.detail, 500),
    },
    gemini: keyAccount(r.gemini, 'gemini'),
    kie: keyAccount(r.kie, 'kie'),
    jobs: { claude: normalizeJob(jobs.claude, 'claude'), codex: normalizeJob(jobs.codex, 'codex') },
    checkedAt: epochMs(r.checkedAt),
  };
}

/** The state of an API key account (Gemini, Kie.ai). */
function keyAccount(value, engine) {
  const g = value && typeof value === 'object' ? value : {};
  return {
    state: pick(g.state, KEY_STATES, 'none'),
    // Only the last characters ever come from the server; keep it short whatever happens.
    hint: line(g.hint, 12),
    envName: typeof g.envName === 'string' && /^[A-Z_][A-Z0-9_]{0,63}$/.test(g.envName) ? g.envName : KEY_ENV_DEFAULTS[engine],
    // A key saved in the office, even when the environment variable passes before it.
    stored: g.stored === true,
  };
}

/** True when an answer carries the accounts state (logout, gemini-key and kie-key return it). */
export function looksLikeAccounts(res) {
  return !!res && typeof res === 'object' && !Array.isArray(res)
    && ['claude', 'codex', 'gemini', 'kie', 'loginsEnabled'].some((k) => Object.prototype.hasOwnProperty.call(res, k));
}

/** Display name of a subscription ("max" → "Max"; unknown ones capitalised). */
export function planLabel(subscription) {
  const s = line(subscription, 40);
  if (!s) return '';
  const key = s.toLowerCase();
  if (Object.prototype.hasOwnProperty.call(PLANS, key)) return PLANS[key];
  return s[0].toUpperCase() + s.slice(1);
}

/**
 * "À renouveler dans N jours" when the Claude refresh token expires within RENEW_WARN_DAYS.
 * @param {number|null} expiresAt epoch ms
 * @param {number} [now]
 * @returns {{days: number, expired: boolean, text: string}|null}
 */
export function renewalWarning(expiresAt, now = Date.now()) {
  const at = epochMs(expiresAt);
  if (!at) return null;
  const left = at - now;
  if (left >= RENEW_WARN_DAYS * DAY_MS) return null;
  if (left <= 0) return { days: 0, expired: true, text: t('adm.acc.renew.expired') };
  const days = Math.floor(left / DAY_MS);
  return { days, expired: false, text: days < 1 ? t('adm.acc.renew.today') : t('adm.acc.renew.in', { n: days }) };
}

const TONES = {
  claude: { account: ['success', 'ok'], token: ['amber', 'warn'], none: ['danger', 'error'], unknown: ['muted', 'off'], error: ['danger', 'error'] },
  codex: { chatgpt: ['success', 'ok'], apikey: ['info', 'ok'], none: ['neutral', 'off'], unknown: ['muted', 'off'], error: ['danger', 'error'] },
  gemini: { stored: ['success', 'ok'], env: ['success', 'ok'], none: ['neutral', 'off'], unknown: ['muted', 'off'] },
  kie: { stored: ['success', 'ok'], env: ['success', 'ok'], none: ['neutral', 'off'], unknown: ['muted', 'off'] },
};

/**
 * Everything a card shows for one account: badge, status sentence, detail, warnings and buttons.
 * @param {'claude'|'codex'|'gemini'|'kie'} engine
 * @param {object} acc the engine's part of normalizeAccounts()
 * @param {{loginsEnabled?: boolean, mock?: boolean, job?: object|null, now?: number, verified?: string|null}} [opts]
 *   verified: the engine's checked state (GET /api/engines `state`) for the key accounts (Gemini,
 *   Kie.ai, Codex through CODEX_API_KEY)
 * @returns {{state: string, tone: string, dot: string, badge: string, text: string, detail: string,
 *   renew: object|null, tokenVar: boolean, jobActive: boolean, connect: string|null,
 *   connectAria: string|null, logout: boolean, editKey: boolean, removeKey: boolean,
 *   alsoStored: boolean}} connectAria names the account when the button text does not
 *   ("Reconnecter"); alsoStored: a Gemini key is saved but the variable passes before it
 */
export function accountView(engine, acc = {}, { loginsEnabled = false, mock = false, job = null, now = Date.now(), verified = null } = {}) {
  const table = Object.prototype.hasOwnProperty.call(TONES, engine) ? TONES[engine] : TONES.claude;
  const state = Object.prototype.hasOwnProperty.call(table, acc.state) ? acc.state : 'unknown';
  let [tone, dot] = table[state];
  // A key that is there is not a key accepted: green only once the engine check accepted it
  // (`verified`: the engine's state, GET /api/engines), red when it refused it.
  const keyThere = KEY_ENGINES.includes(engine) ? state === 'stored' || state === 'env' : engine === 'codex' && state === 'apikey' && acc.keyVarSet === true;
  if (keyThere && !mock) {
    [tone, dot] = verified === 'ok' ? ['success', 'ok'] : verified === 'disconnected' || verified === 'error' ? ['danger', 'error'] : ['info', 'off'];
  }
  const canLogin = loginsEnabled && !mock && LOGIN_ENGINES.includes(engine);
  const jobActive = isJobActive(job);
  const out = {
    state, tone, dot,
    badge: t(`adm.acc.badge.${engine}.${state}`),
    text: '',
    detail: ['unknown', 'error'].includes(state) ? line(acc.detail, 500) : '',
    renew: null,
    tokenVar: false,
    jobActive: canLogin && jobActive,
    connect: null,
    connectAria: null,
    logout: false,
    editKey: KEY_ENGINES.includes(engine) && !mock,
    removeKey: false,
    alsoStored: false,
  };
  if (KEY_ENGINES.includes(engine)) {
    out.alsoStored = !mock && state === 'env' && acc.stored === true;
    // A key saved here stays removable while the variable hides it.
    out.removeKey = !mock && (state === 'stored' || out.alsoStored);
    out.text = state === 'stored' ? t(`adm.acc.${engine}.stored`, { hint: acc.hint || '' }).trim()
      : state === 'env' ? t(loginsEnabled ? `adm.acc.${engine}.env` : `adm.acc.${engine}.envLocal`, { env: acc.envName || KEY_ENV_DEFAULTS[engine] })
        : t(`adm.acc.${engine}.${state}`);
    return out;
  }
  const connected = engine === 'claude' ? state === 'account' : state === 'chatgpt';
  const who = connected ? [acc.email, planLabel(acc.subscription)].filter(Boolean) : [];
  const textKey = engine === 'codex' && state === 'apikey' && acc.keyVarSet === true ? 'adm.acc.codex.apikeyVar' : `adm.acc.${engine}.${state}`;
  out.text = [t(textKey), ...who].join(' · ');
  if (engine === 'claude') {
    out.tokenVar = acc.tokenVarSet === true;
    if (state === 'account') out.renew = renewalWarning(acc.refreshExpiresAt, now);
  }
  if (canLogin) {
    out.connect = out.jobActive ? t('adm.acc.resume') : connected ? t('adm.acc.reconnect') : t(`adm.acc.${engine}.connect`);
    if (out.jobActive || connected) {
      out.connectAria = t(out.jobActive ? 'adm.acc.resumeAria' : 'adm.acc.reconnectAria', { name: t(`adm.acc.name.${engine}`) });
    }
    out.logout = engine === 'claude'
      ? state === 'account' || (state === 'token' && !out.tokenVar)
      : state === 'chatgpt' || (state === 'apikey' && acc.keyVarSet !== true);
  }
  return out;
}

// ---------------------------------------------------------------------------------------------
// Checks mirrored from the server

/**
 * The Claude code as the server wants it: trimmed, 1 to CODE_MAX characters, one line, no
 * control character.
 * @returns {{ok: boolean, value: string, error: string|null}}
 */
export function validateLoginCode(v) {
  const value = str(v).trim();
  if (!value) return { ok: false, value: '', error: t('adm.acc.code.required') };
  if (value.length > CODE_MAX) return { ok: false, value: '', error: t('adm.acc.code.tooLong', { max: CODE_MAX }) };
  if (CONTROL_RE.test(value)) return { ok: false, value: '', error: t('adm.acc.code.oneLine') };
  return { ok: true, value, error: null };
}

/**
 * A Gemini API key as the server wants it (trimmed, GEMINI_KEY_RE).
 * @returns {{ok: boolean, value: string, error: string|null}}
 */
export function validateGeminiKey(v) {
  const value = str(v).trim();
  if (!value) return { ok: false, value: '', error: t('adm.acc.gemini.keyRequired') };
  if (!GEMINI_KEY_RE.test(value)) return { ok: false, value: '', error: t('adm.acc.gemini.keyInvalid') };
  return { ok: true, value, error: null };
}

/**
 * A Kie.ai API key as the server wants it (trimmed, KIE_KEY_RE).
 * @returns {{ok: boolean, value: string, error: string|null}}
 */
export function validateKieKey(v) {
  const value = str(v).trim();
  if (!value) return { ok: false, value: '', error: t('adm.acc.kie.keyRequired') };
  if (!KIE_KEY_RE.test(value)) return { ok: false, value: '', error: t('adm.acc.kie.keyInvalid') };
  return { ok: true, value, error: null };
}

// ---------------------------------------------------------------------------------------------
// Manual procedure

/** Runs a command as the office's user with its HOME (docs/DEPLOY-RAILWAY.md §7). */
const AS_OFFICE = 'setpriv --reuid=node --regid=node --init-groups env HOME=/data/home';

/**
 * The Terminal steps of the hosted instance (`railway ssh`, docs/DEPLOY-RAILWAY.md §7), each
 * `{title, commands?: string[], hint?: string}` like engineHelp().
 * @returns {Array<{title: string, commands?: string[], hint?: string}>}
 */
export function manualSteps() {
  return [
    { title: t('adm.acc.manual.install'), commands: ['brew install railway'], hint: t('adm.acc.manual.installHint') },
    { title: t('adm.acc.manual.link'), commands: ['railway login', 'railway link'], hint: t('adm.acc.manual.linkHint') },
    { title: t('adm.acc.manual.ssh'), commands: ['railway ssh'], hint: t('adm.acc.manual.sshHint') },
    { title: t('adm.acc.manual.claude'), commands: [`cd /data/home && ${AS_OFFICE} claude auth login --claudeai`], hint: t('adm.acc.manual.claudeHint') },
    { title: t('adm.acc.manual.check'), commands: [`${AS_OFFICE} claude mcp list`], hint: t('adm.acc.manual.checkHint') },
    { title: t('adm.acc.manual.codex'), commands: [`${AS_OFFICE} codex login --device-auth`], hint: t('adm.acc.manual.codexHint') },
    { title: t('adm.acc.manual.exit'), commands: ['exit'] },
  ];
}

/**
 * The Terminal steps when the office does not log the accounts in itself (engineLogins off: the
 * Mac mini, whose HOME holds the owner's own logins — docs/DEPLOY-MACMINI.md §4), same shape as
 * manualSteps().
 * @returns {Array<{title: string, commands?: string[], hint?: string}>}
 */
export function localSteps() {
  return [
    { title: t('adm.acc.local.claude'), commands: ['claude'], hint: t('adm.eng.hint.claude.login') },
    { title: t('adm.acc.local.claudeCheck'), commands: ['claude -p "Réponds seulement : OK"', 'claude mcp list'], hint: t('adm.eng.hint.claude.check') },
    { title: t('adm.acc.local.codex'), commands: ['codex login'], hint: t('adm.eng.hint.codex.login') },
    { title: t('adm.acc.local.codexCheck'), commands: ['codex login status'] },
  ];
}
